import styled from '@emotion/styled';
import { Modal } from 'react-bootstrap';
import { PageTitle } from '../../../_metronic/layout/core';
import { useEffect, useState, useMemo } from 'react';
import * as API from '../../api';
import LogDetailsModal from './LogDetailsModal';
import { isSuperAdmin } from '../../modules/auth/core/Auth';
import StatsCard from './StatsCard';
import SearchInput from './SearchInput';
import FilterBar from './FilterBar';
import LogTable from './LogTable';
import Pagination from './Pagination';
import { Activity, Users, FileText, Download, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';

const BRIEFING_AGENT_LOG_TYPE = 'briefing-promocoes-agent-chat';

const parseLogJson = (value: any) => {
  if (!value) return null;
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

const isBriefingAgentLog = (item: any) => {
  return item?.type === BRIEFING_AGENT_LOG_TYPE || item?.json?.source === 'briefing-promocoes-agent';
};

const normalizeAgentMessages = (json: any): any[] => {
  if (Array.isArray(json?.conversation?.messages)) return json.conversation.messages;
  if (Array.isArray(json?.conversation?.exchange)) return json.conversation.exchange;
  if (Array.isArray(json?.conversation?.history)) return json.conversation.history;
  return [];
};

const uniqueBy = (items: any[], getKey: (item: any) => string): any[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mergeBriefingAgentJson = (base: any, next: any) => {
  const baseMessages = normalizeAgentMessages(base);
  const nextMessages = normalizeAgentMessages(next);
  const messages = uniqueBy([...baseMessages, ...nextMessages], (message) => (
    `${message.role || ''}|${message.timestamp || ''}|${message.content || ''}`
  )).sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));
  const baseInteractions = Array.isArray(base?.form?.interactions) ? base.form.interactions : [];
  const nextInteractions = Array.isArray(next?.form?.interactions) ? next.form.interactions : [];
  const interactions = [...baseInteractions, ...nextInteractions];
  const filledValues = {
    ...(base?.form?.filledValues || base?.form?.suggestedValues || {}),
    ...(next?.form?.filledValues || next?.form?.suggestedValues || {}),
  };
  const filledFieldKeys = Array.from(new Set([
      ...(base?.form?.filledFieldKeys || base?.form?.changedFieldKeys || base?.form?.suggestedFieldKeys || []),
      ...(next?.form?.filledFieldKeys || next?.form?.changedFieldKeys || next?.form?.suggestedFieldKeys || []),
    ]));

  return {
    ...base,
    ...next,
    startedAt: base?.startedAt || next?.startedAt,
    lastInteractionAt: next?.lastInteractionAt || base?.lastInteractionAt,
    conversation: {
      totalMessages: messages.length,
      totalExchanges: interactions.length || Math.ceil(messages.length / 2),
      messages,
    },
    form: {
      ...(base?.form || {}),
      ...(next?.form || {}),
      filledValues,
      filledFieldKeys,
      interactions,
    },
  };
};

const groupBriefingAgentLogs = (logs: any[]) => {
  const grouped = new Map<string, any>();
  const result: any[] = [];

  logs.forEach((item) => {
    if (!isBriefingAgentLog(item) || !item.json?.chatId) {
      result.push(item);
      return;
    }

    const key = `${item.user_id || item.user || 'user'}-${item.type}-${item.json.chatId}`;
    const existing = grouped.get(key);

    if (!existing) {
      const clone = { ...item, groupedLogIds: [item.id_log] };
      grouped.set(key, clone);
      result.push(clone);
      return;
    }

    const latest = new Date(existing.created_at) >= new Date(item.created_at) ? existing : item;
    const mergedJson = mergeBriefingAgentJson(existing.json, item.json);
    Object.assign(existing, {
      ...latest,
      id_log: existing.id_log,
      groupedLogIds: Array.from(new Set([...(existing.groupedLogIds || []), item.id_log])),
      json: mergedJson,
      data: JSON.stringify(mergedJson),
    });
  });

  return result;
};

const ScreenStyle = styled.div`
  position: relative;

  .action-btn {
    transition: all 0.2s ease;
  }

  .action-btn:hover {
    transform: scale(1.1);
  }

  .hover-elevate-up {
    transition: all 0.2s ease;
  }

  .hover-elevate-up:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  .react-select-styled .react-select__control {
    border: 1px solid #e4e6ef;
    background-color: #f5f8fa;
    min-height: 42px;
  }

  .react-select-styled .react-select__control:hover {
    border-color: #b5b5c3;
  }

  .react-select-styled .react-select__control--is-focused {
    border-color: #009ef7;
    box-shadow: 0 0 0 1px #009ef7;
  }

  .preloader {
    color: #009ef7;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LogsScreen: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<any>([]);
  const [users, setUsers] = useState<any>([]);
  const [types, setTypes] = useState<any>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<any>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsModal, showDetailsModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadUsers();
    loadTypes();
    loadData();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await API.listUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadTypes = async () => {
    try {
      const typesResponse = await API.listLogTypes();
      setTypes(typesResponse.data);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);

      const filters: any = {
        page: 1,
        limit: 1000,
        sortBy: sortField,
        sortOrder: sortDirection.toUpperCase() as 'ASC' | 'DESC',
      };

      if (startDate) {
        filters.startDate = startDate.toISOString().split('T')[0];
      }

      if (endDate) {
        filters.endDate = endDate.toISOString().split('T')[0];
      }

      const response = await API.listLogs(selectedUser?.value, selectedType?.value, filters);

      response.data.forEach((item: any) => {
        const parsed = parseLogJson(item.data);
        if (parsed && Object.keys(parsed).length > 0) {
          item.json = parsed;
        }
      });
      setData(groupBriefingAgentLogs(response.data));
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedUser, selectedType, startDate, endDate, sortField, sortDirection]);

  useEffect(() => {
    showDetailsModal(selectedItem != null);
  }, [selectedItem]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleClearFilters = () => {
    setSelectedUser(null);
    setSelectedType(null);
    setStartDate(null);
    setEndDate(null);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(paginatedData.map((item) => item.id_log));
    } else {
      setSelectedIds([]);
    }
  };



  const filteredData = useMemo(() => {
    let filtered = [...data];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) => {
          const jsonText = item.json ? JSON.stringify(item.json).toLowerCase() : '';
          return (
            item.user?.toLowerCase().includes(search) ||
            item.name?.toLowerCase().includes(search) ||
            item.type?.toLowerCase().includes(search) ||
            jsonText.includes(search)
          );
        }
      );
    }

    return filtered;
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const stats = useMemo(() => {
    const uniqueUsers = new Set(filteredData.map((item) => item.user)).size;
    const uniqueTypes = new Set(filteredData.map((item) => item.type)).size;

    return {
      total: filteredData.length,
      users: uniqueUsers,
      types: uniqueTypes,
    };
  }, [filteredData]);

  const handleExport = () => {
    const exportData = filteredData.map((item) => ({
      Usuário: item.user,
      Descrição: item.name,
      Tipo: item.type,
      Data: new Date(item.created_at).toLocaleString('pt-BR'),
      Dados: item.data.toString() !== '{}' ? JSON.stringify(item.json) : '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logs');

    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `logs_${date}.xlsx`);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (!isSuperAdmin()) {
    return null;
  }

  return (
    <ScreenStyle>
      <PageTitle>Histórico dos Usuários</PageTitle>

      <Modal
        id="modal_message"
        tabIndex={-1}
        dialogClassName={`modal-dialog modal-dialog-centered ${isBriefingAgentLog(selectedItem) ? 'mw-1200px' : 'mw-900px'}`}
        show={detailsModal}
        onHide={() => setSelectedItem(null)}
      >
        <LogDetailsModal data={selectedItem} onClose={() => setSelectedItem(null)} />
      </Modal>

      {/*<div className="row g-4 mb-4">
        <div className="col-xl-4 col-md-6">
          <StatsCard
            title="Total de Logs"
            value={stats.total.toLocaleString('pt-BR')}
            icon={<Activity size={24} />}
            color="#009ef7"
          />
        </div>
        <div className="col-xl-4 col-md-6">
          <StatsCard
            title="Usuários Ativos"
            value={stats.users.toLocaleString('pt-BR')}
            icon={<Users size={24} />}
            color="#50cd89"
          />
        </div>
        <div className="col-xl-4 col-md-6">
          <StatsCard
            title="Tipos de Logs"
            value={stats.types.toLocaleString('pt-BR')}
            icon={<FileText size={24} />}
            color="#ffc700"
          />
        </div>
      </div>*/}

      <FilterBar
        users={users}
        types={types}
        selectedUser={selectedUser}
        selectedType={selectedType}
        startDate={startDate}
        endDate={endDate}
        onUserChange={setSelectedUser}
        onTypeChange={setSelectedType}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onClearFilters={handleClearFilters}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <div className="card mt-4">
        <div className="card-header border-0 pt-6">
          <div className="card-title flex-grow-1" style={{ maxWidth: '100%' }}>
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por usuário, descrição ou tipo..."
            />
          </div>
          <div className="card-toolbar d-flex gap-2">
            {selectedIds.length > 0 && (
              <>
                <span className="badge badge-light-primary fs-6 py-3 px-4">
                  {selectedIds.length} selecionado(s)
                </span>
                <button
                  className="btn btn-light-danger btn-sm d-flex align-items-center gap-2"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 size={16} />
                  Deletar Selecionados
                </button>
              </>
            )}
            {/*<button
              className="btn btn-light-success btn-sm d-flex align-items-center gap-2"
              onClick={handleExport}
              disabled={filteredData.length === 0}
            >
              <Download size={16} />
              Exportar Excel
            </button>*/}
          </div>
        </div>

        <div className="card-body py-4">
          {isLoading ? (
            <div className="justify-content-center text-center mb-4 py-10">
              <p className="mb-5 text-gray-600 fs-5">Carregando logs...</p>
              <div className="preloader"></div>
            </div>
          ) : (
            <>
              <LogTable
                data={paginatedData}
                onViewDetails={setSelectedItem}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
              />
              {filteredData.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filteredData.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </ScreenStyle>
  );
};

export default LogsScreen;
